import React, { createContext, useState } from "react";

// Create Context
export const CartContext = createContext();

// 🎯 Helper function to check for the exact item match across all identifiers
const isSameItem = (item1, item2) => (
    item1.id === item2.id &&
    item1.category === item2.category &&
    (item1.subcategory === item2.subcategory)
);

// Cart Provider Component
export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);

    /**
     * Helper function to update quantity for a specific product item (using multi-key match).
     * Now accepts the full product object for precise matching.
     */
    const updateQuantity = (productToUpdate, amount) => {
        setCartItems((prevItems) => {
            return prevItems.map((item) => {
                if (isSameItem(item, productToUpdate)) {
                    const newQuantity = item.quantity + amount;

                    // Logic to ensure quantity doesn't drop below 1
                    if (newQuantity < 1) {
                        // If quantity falls below 1, you can choose to remove the item here
                        // For now, it defaults to keeping quantity at 1
                        return { ...item, quantity: 1 };
                    }
                    return { ...item, quantity: newQuantity };
                }
                return item;
            });
        });
    };

    const increaseQuantity = (productToUpdate) => updateQuantity(productToUpdate, 1);

    const decreaseQuantity = (productToUpdate) => updateQuantity(productToUpdate, -1);

    /**
     * Adds a product to the cart or increases the quantity if the exact listing exists.
     */
    const addToCart = (product) => {
        setCartItems((prevItems) => {
            const existingItem = prevItems.find((item) => isSameItem(item, product));

            if (existingItem) {
                // If the exact item exists, increase quantity
                return prevItems.map((item) =>
                    isSameItem(item, product) ? { ...item, quantity: item.quantity + 1 } : item
                );
            } else {
                // Add new item. Ensure category and subcategory are properly stored.
                const itemToStore = {
                    ...product,
                    quantity: product.quantity || 1,
                    category: product.category,
                    subcategory: product.subcategory || null,
                };
                return [...prevItems, itemToStore];
            }
        });
    };

    const removeFromCart = (productToRemove) => {
        setCartItems((prevItems) =>
            prevItems.filter((item) => !isSameItem(item, productToRemove))
        );
    };

    // Clear cart completely
    const clearCart = () => {
        setCartItems([]);
    };

    return (
        <CartContext.Provider
            value={{
                cartItems,
                addToCart,
                removeFromCart,
                clearCart,
                increaseQuantity,
                decreaseQuantity
            }}
        >
            {children}
        </CartContext.Provider>
    );
};